import React, { useContext, useEffect, useState } from 'react'
import {
	FormBuilder,
	formKeySelector,
	updateControlState,
} from 'formBuilder/src'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { FormFactory } from 'formBuilder/formFactory'
import { CustomerForm } from 'constants/enums/formTypes'
import { AllCountries } from 'constants/enums/countryTypes'
import ExternalServices from 'services/externalServices'
import { VirtualPagePaths } from 'constants/values/gtmConstants'

import './FillPersonalDetail.scss'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import updateApplicationServices from 'services/updateApplicationServices'
import getApplicationServices from 'services/getApplicationServices'
import { LifeCycleStatus } from 'constants/enums/lifeCycleStatus'
import {
	IKycCurrencyAndValues,
	kycCurrenciesAndValues,
} from 'constants/values/kycValues'
import { OccupationType } from 'constants/enums/occupationTypes'
import { getOccupationTypeOptions } from 'constants/values/italyOccupationCategories'
import { getIdentificationTypeOptions } from 'constants/values/italyIdentificationTypes'
import { OccupationCategory } from 'constants/enums/occupationCategoryTypes'
import { ProvincesIt } from 'constants/enums/provinces'
import { IUpdateApplicationInformation } from 'interfaces/i-update-application'
import { TranslationFile } from 'i18n'
import { DateTime } from 'luxon'

const FORM_NAME = 'applicationDetailsForm'

// set fields to null if the enum values we received aren't valid för Italy
function sanitizeApplicationData(
	data: IApplicationInformation,
): IApplicationInformation {
	const italianKycData = kycCurrenciesAndValues.find(
		(e) => e.country === 'it',
	) as IKycCurrencyAndValues

	const italianOccupations = italianKycData.occupation?.map(
		(occupation: number) => OccupationType[occupation],
	) // needs special treatment as OccupationType is a numeric and not a string enum
	if (
		data.employment?.occupationType &&
		italianOccupations &&
		!italianOccupations.includes(data.employment?.occupationType.toString())
	) {
		data.employment.occupationType = null
	}

	if (
		data.accommodation?.type &&
		!italianKycData.accommodationType?.includes(data.accommodation?.type)
	) {
		data.accommodation.type = null
	}

	if (
		data.address?.province &&
		!Object.keys(ProvincesIt).includes(data.address.province)
	) {
		data.address.province = undefined
	}

	return data
}

// The datepicker control's stored value is either an untouched locale-formatted string (see formatToStringLocale, 'dd/MM/yyyy') or a JS Date once the user has actually picked a value. px only wants a plain date for these two fields (not a full datetime), so this normalizes either shape down to a 'yyyy-MM-dd' string here rather than in the shared DatePicker component, which other markets/fields still rely on for full datetime values.
function toDateOnlyString(value: unknown): string | null {
	if (!value) return null
	const dt =
		value instanceof Date
			? DateTime.fromJSDate(value)
			: DateTime.fromFormat(String(value), 'dd/MM/yyyy')
	return dt.isValid ? dt.toISODate() : null
}

export const FillPersonalDataIt = () => {
	const { t } = useTranslation()
	const { t: tDropdown } = useTranslation(TranslationFile.Dropdown)
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)
	const { msp, dispatchMsp } = useContext(MspDispatchContext)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<Error | any>()
	const dispatch = useDispatch()

	const formSchema = FormFactory(
		CustomerForm.ApplicationDetailsForm,
		{
			formName: FORM_NAME,
			blockName: FORM_NAME,
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		sanitizeApplicationData(
			loanApplication.applicationData as IApplicationInformation,
		),
	)

	const formStateValues = useSelector(
		formKeySelector(FORM_NAME, 'controlsState'),
	)

	// occupationType's selectable options depend on the occupationCategory field chosen above it; the form-builder's declarative conditions can only show/hide fields, not swap their options, so this dispatches a direct control-state update instead.
	const occupationCategoryValue = formStateValues?.occupationCategory?.value
	useEffect(() => {
		if (!occupationCategoryValue) return

		const category = OccupationCategory[
			occupationCategoryValue as keyof typeof OccupationCategory
		] as unknown as OccupationCategory
		const newOptions = getOccupationTypeOptions(category, tDropdown)
		const currentOccupationTypeValue =
			formStateValues?.occupationType?.value

		dispatch(
			updateControlState({
				formName: FORM_NAME,
				controlName: 'occupationType',
				options: newOptions,
				value: newOptions.some(
					(o) => o.value === currentOccupationTypeValue,
				)
					? currentOccupationTypeValue
					: '',
			}),
		)
	}, [occupationCategoryValue]) // eslint-disable-line react-hooks/exhaustive-deps

	// identificationType's selectable options depend on whether the chosen nationality is an EU country or not (EU: IdCard/Passport, non-EU: ResidenceTitle only).
	const nationalityValue = formStateValues?.nationality?.value
	useEffect(() => {
		if (!nationalityValue) return

		const newOptions = getIdentificationTypeOptions(
			nationalityValue,
			tDropdown,
		)
		const currentIdentificationTypeValue =
			formStateValues?.identificationType?.value

		dispatch(
			updateControlState({
				formName: FORM_NAME,
				controlName: 'identificationType',
				options: newOptions,
				// Non-EU only ever has the single ResidenceTitle option, so pre-select it and lock the field rather than leaving an editable dropdown with just one choice.
				readonly: newOptions.length === 1,
				value: newOptions.some(
					(o) => o.value === currentIdentificationTypeValue,
				)
					? currentIdentificationTypeValue
					: newOptions.length === 1
						? newOptions[0].value
						: '',
			}),
		)
	}, [nationalityValue]) // eslint-disable-line react-hooks/exhaustive-deps

	// TEMP DEV ONLY: jumps straight to step 2 on load so step 1 doesn't need refilling on every reload while developing on step 2 fields.
	// useEffect(() => {
	// 	dispatch(setFormStepAction(FORM_NAME, 1))
	// }, []) // eslint-disable-line react-hooks/exhaustive-deps

	// The two form-builder steps aren't reflected as separate entries in msvItaly's multistep list, so their own page view events are pushed from here instead.
	const step = useSelector(formKeySelector(FORM_NAME, 'step')) ?? 0

	useEffect(() => {
		ExternalServices.pushVirtualPageView(
			step === 0
				? VirtualPagePaths.IT.PERSONAL_DATA_STEP_1
				: VirtualPagePaths.IT.PERSONAL_DATA_STEP_2,
		)
	}, [step])

	useEffect(
		() => {
			dispatchLoanApplication({
				type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
				payload: {
					...loanApplication,
					updateApplicationData: {
						...loanApplication.updateApplicationData,
						applicant: {
							...loanApplication.updateApplicationData?.applicant,
							personalInfo: {
								...loanApplication.updateApplicationData
									?.applicant?.personalInfo,
								nationality: formStateValues?.nationality.value,
								residencePermit: formStateValues
									?.residencePermit.visible
									? formStateValues?.residencePermit.value
									: null,
								residencePermitUntil: formStateValues
									?.residencePermitUntil.visible
									? toDateOnlyString(
											formStateValues
												?.residencePermitUntil.value,
										)
									: null,
							},
							address: {
								...loanApplication.updateApplicationData
									?.applicant?.address,
								streetType: formStateValues?.streetType.value,
								street: formStateValues?.street.value,
								streetNumber:
									formStateValues?.streetNumber.value,
								zip: formStateValues?.zip.value,
								city: formStateValues?.city.value,
								province: formStateValues?.province.value,
							},
							accommodation: {
								...loanApplication.updateApplicationData
									?.applicant.accommodation,
								since: toDateOnlyString(
									formStateValues?.accomodationSince.value,
								),
								childrenInFamily:
									formStateValues?.childrenInFamily.value,
								type: formStateValues?.accomodationType.value,
							},
							employment: {
								...loanApplication.updateApplicationData
									?.applicant.employment,
								occupationType:
									formStateValues?.occupationCategory
										.value === 'FixedTerm'
										? OccupationType.FixedTerm
										: formStateValues?.occupationType.value,
								employmentType: formStateValues?.employmentType
									.visible
									? formStateValues?.employmentType.value
									: null,
								monthlyNetIncome:
									formStateValues?.monthlyNetIncome.value,
								numberOfPayslips:
									formStateValues?.numberOfPayslips.value,
								employedSincePeriod: formStateValues
									?.employedSincePeriod.visible
									? formStateValues?.employedSincePeriod.value
									: null,
							},
							loans: {
								...loanApplication.updateApplicationData
									?.applicant?.loans,
								houseMonthlyCost:
									formStateValues?.houseMonthlyCost.value,
							},
							additionalInfo: {
								...loanApplication.updateApplicationData
									?.applicant?.additionalInfo,
								politicallyExposedPerson:
									formStateValues?.pep.value,
								connectedToPep:
									formStateValues?.connectedToPep.value,
							},
							identification: {
								...loanApplication.updateApplicationData
									?.applicant?.identification,
								identificationType:
									formStateValues?.identificationType.value,
								number: formStateValues?.identificationNumber
									.value,
							},
						},
						purposeOfLoan: formStateValues?.purposeOfLoan.value,
					},
				},
			})
		},
		[formStateValues], // eslint-disable-line react-hooks/exhaustive-deps
	)

	function addFixedValuesToApplicationData(
		data: IUpdateApplicationInformation,
	): IUpdateApplicationInformation {
		if (loanApplication.applicationData?.loanNumber)
			data.applicationId = loanApplication.applicationData?.loanNumber
		if (loanApplication.applicationData?.product)
			data.product = loanApplication.applicationData?.product
		data.applicant.personalInfo.citizenship = data.applicant.personalInfo
			.nationality
			? [data.applicant.personalInfo.nationality]
			: []
		data.applicant.address.countryOfResidence =
			'IT' as unknown as AllCountries

		return data
	}

	const submitUpdatedApplicationData = async () => {
		if (!loanApplication.updateApplicationData) return

		const updatedData = addFixedValuesToApplicationData(
			loanApplication.updateApplicationData,
		)

		setLoading(true)
		setError(null)
		try {
			await updateApplicationServices
				.updateLoanApplicationIt(updatedData, t)
				.then(async (response) => {
					if (response.status === 200) {
						const appInfo =
							await getApplicationServices.GetApplicationInformation(
								loanApplication?.applicationData?.loanNumber!,
								t,
							)
						loanApplication.setApplicationData(appInfo)
						const stepsToSkip =
							appInfo?.lifeCycleStatus ===
							LifeCycleStatus.WaitingForBankData
								? 1
								: 2
						dispatchMsp({
							type: MSP_ACTIONS.SET_INDEX,
							payload: {
								index: msp.index + stepsToSkip,
							},
						})
					}
				})
		} catch (error) {
			console.warn(error)
			setError(error)
			setLoading(false)
		}
	}

	return (
		<div className="update-personal-data">
			{loading ? (
				<div className="col-spinner">
					<CircleSpinner />
				</div>
			) : (
				<>
					{error ? (
						<ErrorBox message={t('ErrorMsg')} />
					) : (
						<>
							<p>{t('UpdatePersonalDataDesc')}</p>
							<div className="update-personal-data-section">
								{formSchema ? (
									<FormBuilder
										formSchema={formSchema}
										apiError={undefined}
										showErrorsInForm={true}
										showSubmitButton={true}
										onSubmit={() => {
											setTimeout(() => {
												submitUpdatedApplicationData()
											}, 400)
										}}
									/>
								) : (
									<CircleSpinner />
								)}
							</div>
						</>
					)}
				</>
			)}
		</div>
	)
}
